import React from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";

import PageShell from "../common/PageShell";

import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";

import utils from "../../universal/utils";
import Rating3Badge from "../common/Rating3Badge";
import EloBadge from "../common/EloBadge.js";

const TAB_GLOBAL = 0;
const TAB_COUNTRY = 1;
const TAB_FRIENDS = 2;

function getScopeByTab(tab) {
  if (tab === TAB_COUNTRY) return "country";
  if (tab === TAB_FRIENDS) return "friends";
  return "global";
}

export default function LeaderboardPage({ catalog, actions }) {
  const user = catalog?.user;
  const lb = catalog?.leaderboard || {};

  const [tab, setTab] = React.useState(TAB_GLOBAL);
  const [countryInput, setCountryInput] = React.useState(user?.country || "");
  const [countries, setCountries] = React.useState([]);
  const [countriesLoading, setCountriesLoading] = React.useState(false);
  const [selectedCountry, setSelectedCountry] = React.useState(null);
  const [showExplainer, setShowExplainer] = React.useState(false);
  const [myRankHint, setMyRankHint] = React.useState(null);
  const [jumpingToMe, setJumpingToMe] = React.useState(false);

  const normalizeCountryKey = React.useCallback((value) => {
    if (!value) return "";
    return String(value).replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  }, []);

  // Keep country input aligned with profile (unless user manually changed it)
  React.useEffect(() => {
    if (!countryInput && user?.country) {
      setCountryInput(user.country);
    }
  }, [user?.country]);

  // Fetch Europe countries for the dropdown (on-demand)
  React.useEffect(() => {
    if (!user?.userId) return;
    if (countriesLoading) return;
    if (countries.length > 0) return;

    const basePath = utils.getEdgeBasePath();
    setCountriesLoading(true);
    fetch(`${basePath}/api/v1/lobby/countries`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.ok && Array.isArray(data.countries)) {
          setCountries(data.countries);
        } else {
          setCountries([]);
        }
      })
      .catch(() => setCountries([]))
      .finally(() => setCountriesLoading(false));
  }, [user?.userId, countriesLoading, countries.length]);

  // Resolve initial selected country from profile once countries are loaded
  React.useEffect(() => {
    if (!user?.country) return;
    if (!Array.isArray(countries) || countries.length === 0) return;
    if (selectedCountry) return;

    const key = normalizeCountryKey(user.country);
    if (!key) return;

    const match = countries.find((c) => {
      return (
        normalizeCountryKey(c.displayName) === key ||
        normalizeCountryKey(c.code2) === key ||
        normalizeCountryKey(c.code3) === key
      );
    });
    if (match) {
      setSelectedCountry(match);
      setCountryInput(match.displayName);
    }
  }, [countries, user?.country, selectedCountry, normalizeCountryKey]);

  // Initial load (logged-in only)
  React.useEffect(() => {
    if (!user?.userId) return;
    actions.getLeaderboard({ scope: "global", limit: 50, offset: 0 });
  }, [user?.userId]);

  const handleTabChange = (event, nextTab) => {
    setTab(nextTab);
    if (!user?.userId) return;

    const scope = getScopeByTab(nextTab);
    if (scope === "country") {
      actions.getLeaderboard({
        scope,
        country: selectedCountry?.displayName || countryInput || user?.country,
        limit: 50,
        offset: 0,
      });
      return;
    }

    actions.getLeaderboard({
      scope,
      limit: scope === "friends" ? 200 : 50,
      offset: 0,
    });
  };

  const handleCountryRefresh = () => {
    if (!user?.userId) return;
    actions.getLeaderboard({
      scope: "country",
      country: selectedCountry?.displayName || countryInput || user?.country,
      limit: 50,
      offset: 0,
    });
  };

  const handleLoadMore = () => {
    if (!user?.userId) return;
    const nextOffset = (lb?.offset || 0) + (lb?.limit || 50);
    const scope = lb?.scope || getScopeByTab(tab);

    actions.getLeaderboard({
      scope,
      country:
        scope === "country"
          ? selectedCountry?.displayName || lb?.country?.displayName || countryInput || user?.country
          : undefined,
      limit: lb?.limit || 50,
      offset: nextOffset,
      append: true,
    });
  };

  const isLoading = lb?.loading === true;
  const hasRows = Array.isArray(lb?.rows) && lb.rows.length > 0;
  const scope = lb?.scope || getScopeByTab(tab);
  const myRow = (lb?.rows || []).find((r) => r?.userId && user?.userId && r.userId === user.userId) || null;
  const myRank = myRow?.rank || myRankHint;

  const showLoadMore =
    !isLoading &&
    lb?.total != null &&
    lb?.limit != null &&
    lb?.offset != null &&
    (lb.offset + lb.limit) < lb.total;

  const handleJumpToMe = async () => {
    if (!user?.userId) return;
    const activeScope = scope;

    const limit = activeScope === "friends" ? 200 : (lb?.limit || 50);
    const country =
      activeScope === "country"
        ? (selectedCountry?.displayName || countryInput || user?.country)
        : undefined;

    setJumpingToMe(true);
    try {
      const qs = new URLSearchParams();
      qs.set("scope", activeScope);
      qs.set("limit", String(limit));
      if (country) qs.set("country", country);

      const basePath = utils.getEdgeBasePath();
      const resp = await fetch(`${basePath}/api/v1/leaderboard/me?${qs.toString()}`, { credentials: "include" });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.ok === false) {
        throw new Error(data?.message || data?.error || `Failed to find your rank (HTTP ${resp.status})`);
      }

      if (typeof data?.rank === "number") {
        setMyRankHint(data.rank);
      }

      const offset = typeof data?.offset === "number" ? data.offset : 0;
      actions.getLeaderboard({
        scope: activeScope,
        country,
        limit,
        offset,
        append: false,
      });
    } catch (e) {
      actions.leaderboardLoadError &&
        actions.leaderboardLoadError(e?.message || "Failed to find your rank");
    } finally {
      setJumpingToMe(false);
    }
  };

  return (
    <PageShell maxWidth="lg">
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
              Rankings
            </Typography>
            <Typography sx={{ color: "#999999", mt: 0.25, fontSize: 13 }}>
              Global rankings across all supported regions.
            </Typography>
          </Box>

          {scope === "country" && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Autocomplete
                sx={{ minWidth: { xs: 220, sm: 320 } }}
                size="small"
                options={countries}
                value={selectedCountry}
                loading={countriesLoading}
                filterOptions={createFilterOptions({
                  stringify: (opt) => `${opt.displayName} ${opt.code2} ${opt.code3}`,
                  trim: true,
                })}
                getOptionLabel={(opt) => opt?.displayName || ""}
                onChange={(event, nextValue) => {
                  setSelectedCountry(nextValue || null);
                  const nextName = nextValue?.displayName || "";
                  setCountryInput(nextName);
                  if (nextValue?.displayName) {
                    actions.getLeaderboard({
                      scope: "country",
                      country: nextValue.displayName,
                      limit: 50,
                      offset: 0,
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    placeholder="Type to search (e.g. ge → Germany)"
                    onChange={(e) => setCountryInput(e.target.value)}
                  />
                )}
              />
              <Button variant="contained" onClick={handleCountryRefresh}>
                Refresh
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 1.5, display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Button variant="text" onClick={() => setShowExplainer((v) => !v)}>
            {showExplainer ? "Hide ranking details" : "How rankings work"}
          </Button>
          {myRank && (
            <Typography sx={{ fontSize: 13, color: "#cccccc", fontWeight: 700 }}>
              Your rank: {myRank}
            </Typography>
          )}
        </Box>

        <Collapse in={showExplainer}>
          <Box sx={{ mt: 1, mb: 1.5 }}>
            <Paper sx={{ p: 2, borderRadius: 2, backgroundColor: "rgba(255,255,255,0.03)" }} elevation={0}>
              <Typography sx={{ fontWeight: 800, mb: 0.75 }}>
                Ranking rules (POC)
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#cccccc", lineHeight: 1.6 }}>
                - Rankings are ordered by <b>ELO</b> (higher is better).<br />
                - Tie-breaks: nickname (A→Z), then stable id.<br />
                - <b>Global</b> shows all players across every supported region.<br />
                - <b>Country</b> filters by the selected country.<br />
                - <b>Friends</b> includes you + accepted friends.
              </Typography>
              <Divider sx={{ my: 1.25, backgroundColor: "rgba(255,255,255,0.08)" }} />
              <Typography sx={{ fontSize: 12.5, color: "#999999", lineHeight: 1.6 }}>
                Rankings include players from all supported regions (Europe, Middle East, Americas, Asia-Pacific).
              </Typography>
            </Paper>
          </Box>
        </Collapse>

        <Box sx={{ mt: 2 }}>
          <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="Global" />
            <Tab label="Country" />
            <Tab label="Friends" />
          </Tabs>
        </Box>

        <Box sx={{ mt: 2 }}>
          {!user && (
            <Typography>Please wait...</Typography>
          )}

          {user && isLoading && !hasRows && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={22} />
              <Typography>Loading...</Typography>
            </Box>
          )}

          {user && lb?.error && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ color: "#E8A33D", fontWeight: 600 }}>
                {lb.error}
              </Typography>
            </Box>
          )}

          {user && !isLoading && !hasRows && !lb?.error && (
            <Typography sx={{ color: "#999999" }}>
              No leaderboard data available.
            </Typography>
          )}

          {user && hasRows && (
            <React.Fragment>
              <TableContainer sx={{ borderRadius: 2, border: "1px solid rgba(255,255,255,0.08)" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 64, fontWeight: 800 }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Player</TableCell>
                      <TableCell sx={{ width: 120, fontWeight: 800 }}>ELO</TableCell>
                      <TableCell sx={{ width: 100, fontWeight: 800 }}>Rating</TableCell>
                      <TableCell sx={{ width: 200, fontWeight: 800 }}>Country</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lb.rows.map((row) => {
                      const isMe = row?.userId && user?.userId && row.userId === user.userId;
                      const profileLink = row?._id ? `/profile/${row._id}` : null;

                      return (
                        <TableRow
                          key={`${row?.userId || row?._id || row?.rank}`}
                          hover
                          sx={
                            isMe
                              ? { backgroundColor: "rgba(232, 163, 61, 0.10)" }
                              : undefined
                          }
                        >
                          <TableCell sx={{ fontWeight: 700 }}>
                            {row?.rank || "-"}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                              <Avatar
                                src={row?.picture || undefined}
                                alt={row?.nickname || "Player"}
                                sx={{ width: 28, height: 28 }}
                              />
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                {profileLink ? (
                                  <Typography
                                    component={RouterLink}
                                    to={profileLink}
                                    sx={{
                                      color: "inherit",
                                      textDecoration: "none",
                                      fontWeight: 700,
                                      "&:hover": { textDecoration: "underline" },
                                    }}
                                  >
                                    {row?.nickname || "Unknown"}
                                    {isMe ? " (you)" : ""}
                                  </Typography>
                                ) : (
                                  <Typography sx={{ fontWeight: 700 }}>
                                    {row?.nickname || "Unknown"}
                                    {isMe ? " (you)" : ""}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <EloBadge value={row?.elo} size="small" />
                          </TableCell>
                          <TableCell>
                            <Rating3Badge value={row?.rating3} size="small" />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              {row?.flagImageUrl ? (
                                <Box
                                  component="img"
                                  src={row.flagImageUrl}
                                  alt={row?.country || "Country"}
                                  sx={{
                                    width: 22,
                                    height: 16,
                                    borderRadius: "2px",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <Box sx={{ width: 22, height: 16 }} />
                              )}
                              <Typography sx={{ fontSize: 13, color: "#cccccc" }}>
                                {row?.country || "-"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, gap: 2, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 13, color: "#999999" }}>
                  {lb?.total != null ? `Showing ${lb.rows.length} of ${lb.total}` : null}
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {isLoading && hasRows && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography sx={{ fontSize: 13, color: "#999999" }}>Loading…</Typography>
                    </Box>
                  )}
                  <Button variant="outlined" onClick={handleJumpToMe} disabled={jumpingToMe || isLoading}>
                    {jumpingToMe ? "Jumping…" : "Jump to me"}
                  </Button>
                  {showLoadMore && (
                    <Button variant="outlined" onClick={handleLoadMore}>
                      Load more
                    </Button>
                  )}
                </Box>
              </Box>
            </React.Fragment>
          )}
        </Box>
      </Paper>
    </PageShell>
  );
}

LeaderboardPage.propTypes = {
  catalog: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

